/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useReducer } from 'react'
import {
  FooterContentWrapperUI,
  ImpactInterimUI,
  ImpactStandAloneUI,
  MatchDetailsTogglerUI,
  MatchIdUI,
  MatchItemEventDetailsUI,
  MatchItemEventDetailsWrapperUI,
  MatchItemOuterWrapper,
  MatchStatusWrapperUI,
  MatchSummaryWrapperUI,
  PlayerAvatarUI,
  RatingBlockUI,
  TeamCollapsedPlayerRatingAndImpactUI,
  TeamCollapsedSummaryPlayersWrapperUI,
  TeamCollapsedSummaryScoreWinnerUI,
  TeamCollapsedSummaryScoreWrapperUI,
  TeamCollapsedSummaryWrapperUI,
  LockedRatingUI,
  TeamExpandedPlayersWrapperUI,
  TeamExpandedSummaryWrapperUI,
  TeamGameScoreUI,
  PlayerInfoUI,
  TeamsCollapsedSummaryWrapperUI,
  TeamScoreWrapperUI,
  TeamsExpandedSummaryWrapperUI,
  WinLossBadgeUI,
  PlayerSummaryWrapperUI,
  PlayerProfileDataUI,
  PlayerInfoWrapperUI,
  PlayerInfoIntetrnalWrapperUI,
  TeamExpandedPlayerRatingChangeWrapperUI,
} from './Match.web'
import { Match } from '@/types'
import { useMatchRatingLock } from '@/hooks/useRatingLock'
import { calculateMatchStatus, Impact, MatchStatus, useMatchRatingAndImpact, Valence } from './match-card-new-utils'
import { useGetPlayerProfile } from '@/services'
import { MatchPendingActions } from './MatchModals'
import { MatchByPlayType } from './MatchByPlayType'

const MatchContext = createContext<{
  rawMatchData: Match
  isExpanded: boolean
  toggleExpand: () => void
  eventName: string
  eventDate: string
  eventLocation: string
  id: number
  matchId: Match['matchId']
  team1: Match['teams'][0]
  team2: Match['teams'][1]
  eventFormat: Match['eventFormat']
  eloCalculated: boolean
  matchOwner: number
  subjectId: number
  status: MatchStatus
  matchSource: Match['matchSource']
  confirmed: Match['confirmed']
  displayIdentity: Match['displayIdentity']
}>({
  rawMatchData: {} as Match,
  isExpanded: false,
  toggleExpand: () => {},
  eventName: '',
  eventDate: '',
  eventLocation: '',
  id: 0,
  displayIdentity: '' as Match['displayIdentity'],
  matchId: 0 as Match['matchId'],
  team1: {} as Match['teams'][0],
  team2: {} as Match['teams'][1],
  eventFormat: 'SINGLES' as Match['eventFormat'],
  eloCalculated: false,
  matchOwner: 0,
  subjectId: 0,
  status: MatchStatus.INITIALIZATION,
  matchSource: 'USER_REPORTED',
  confirmed: false,
})

const MatchTeamContext = createContext<{
  team: Match['teams'][0] | Match['teams'][1]
  oppositeTeam: Match['teams'][0] | Match['teams'][1]
  teamSlot: number
}>({
  team: {} as Match['teams'][0],
  oppositeTeam: {} as Match['teams'][1],
  teamSlot: 1,
})

const MatchSubjectPlayerContext = createContext<{
  win: boolean
  valence: Valence
  impact: Impact
  preMatchRating: string
  postMatchRating: string
}>({
  win: false,
  valence: 'neutral',
  impact: {} as Impact,
  preMatchRating: '',
  postMatchRating: '',
})

function MatchSubjectPlayerInnerContextProvider({ children }: { children: React.ReactNode }) {
  const { team } = useMatchTeam()
  const { subjectId, isExpanded } = useMatch()
  const playerSlot = team.player1.id === subjectId ? 1 : 2

  const player = playerSlot === 1 ? team.player1 : team.player2

  const playerProfile = useGetPlayerProfile(subjectId?.toString(), isExpanded)

  const { impact, preMatchRating, postMatchRating, valence } = useMatchRatingAndImpact(
    player,
    playerSlot,
    playerProfile
  )

  return (
    <MatchSubjectPlayerContext.Provider
      value={{
        win: team.winner,
        valence,
        impact,
        preMatchRating,
        postMatchRating,
      }}
    >
      {children}
    </MatchSubjectPlayerContext.Provider>
  )
}

function MatchSubjectPlayerContextProvider({ children }: { children: React.ReactNode }) {
  const match = useMatch()
  const { subjectId } = match
  const { team1, team2 } = match
  const teams = [team1, team2]
  const teamIndex = teams.findIndex((t) => t.player1.id === subjectId || t.player2?.id === subjectId)
  const team = teamIndex !== -1 ? teams[teamIndex] : teams[0]

  return (
    <MatchTeamContextProvider team={team} oppositeTeam={teamIndex === 0 ? teams[1] : teams[0]} teamSlot={teamIndex + 1}>
      <MatchSubjectPlayerInnerContextProvider>{children}</MatchSubjectPlayerInnerContextProvider>
    </MatchTeamContextProvider>
  )
}

function MatchTeamContextProvider({
  children,
  team,
  oppositeTeam,
  teamSlot,
}: {
  children: React.ReactNode
  team: Match['teams'][0] | Match['teams'][1]
  oppositeTeam: Match['teams'][0] | Match['teams'][1]
  teamSlot: number
}) {
  return <MatchTeamContext.Provider value={{ team, oppositeTeam, teamSlot }}>{children}</MatchTeamContext.Provider>
}

const MatchPlayerContext = createContext<{
  player: Match['teams'][0]['player1'] | Match['teams'][0]['player2']
  playerSlot: number
  preMatchRating: string
  impact: Impact
  postMatchRating: string
  playerProfile: ReturnType<typeof useGetPlayerProfile>
  valence: Valence
}>({
  player: null as unknown as Match['teams'][0]['player1'],
  playerSlot: 1,
  preMatchRating: '',
  impact: {} as Impact,
  postMatchRating: '',
  valence: 'neutral',
  playerProfile: {} as ReturnType<typeof useGetPlayerProfile>,
})

function MatchPlayerContextProvider({ children, playerSlot }: { children: React.ReactNode; playerSlot: number }) {
  const { team } = useMatchTeam()
  const { isExpanded } = useMatch()

  const player = playerSlot === 1 ? team.player1 : team.player2

  const playerProfile = useGetPlayerProfile(player.id.toString(), isExpanded)

  const { preMatchRating, impact, postMatchRating, valence } = useMatchRatingAndImpact(
    player,
    playerSlot,
    playerProfile
  )

  return (
    <MatchPlayerContext.Provider
      value={{ player, playerSlot, playerProfile, preMatchRating, impact, postMatchRating, valence }}
    >
      {children}
    </MatchPlayerContext.Provider>
  )
}

export function useMatch() {
  return useContext(MatchContext)
}

export function useMatchTeam() {
  return useContext(MatchTeamContext)
}

export function useMatchPlayer() {
  return useContext(MatchPlayerContext)
}

export function useMatchSubjectPlayer() {
  return useContext(MatchSubjectPlayerContext)
}

function MatchContextProvider({
  match,
  playerId,
  children,
}: {
  match: Match
  playerId: number
  children: React.ReactNode
}) {
  const [isExpanded, toggleExpand] = useReducer((prev) => !prev, false)

  const {
    eventDate,
    eventName,
    location: eventLocation,
    id,
    matchId,
    teams,
    eventFormat,
    eloCalculated,
    userId: matchOwner,
    matchSource,
    confirmed,
    displayIdentity,
  } = match

  const team1 = teams[0]
  const team2 = teams[1]

  return (
    <MatchContext.Provider
      value={{
        isExpanded,
        toggleExpand,
        eventDate,
        eventName,
        eventLocation,
        id,
        matchId,
        displayIdentity,
        team1,
        team2,
        eventFormat,
        eloCalculated,
        matchOwner,
        subjectId: playerId,
        status: calculateMatchStatus(match),
        matchSource,
        confirmed,
        rawMatchData: match,
      }}
    >
      {children}
    </MatchContext.Provider>
  )
}

export function MatchItem({ match, playerId }: { match: Match; playerId: number }) {
  return (
    <MatchByPlayType
      matchPlayType={match.matchPlayType}
      matchDate={match.eventDate}
      adjustmentData={{ eventName: match.eventName, match }}
      regular={() => (
        <MatchContextProvider match={match} playerId={playerId}>
          <MatchSubjectPlayerContextProvider>
            <MatchItemOuterWrapper matchId={match.matchId.toString()}>
              <MatchItemHeader />

              <MatchItemBody />

              <MatchPendingActions />

              <MatchItemFooter />
            </MatchItemOuterWrapper>
          </MatchSubjectPlayerContextProvider>
        </MatchContextProvider>
      )}
    />
  )
}

function WinLossBadge() {
  const { win } = useMatchSubjectPlayer()
  return <WinLossBadgeUI win={win} />
}

function RatingChangeBadge() {
  const { status: matchStatus, subjectId, rawMatchData } = useMatch()
  const { valence, impact } = useMatchSubjectPlayer()
  const isRatingLocked = useMatchRatingLock(rawMatchData)

  return (
    <MatchStatusWrapperUI status={matchStatus}>
      {isRatingLocked(subjectId) ? (
        <LockedRatingUI />
      ) : (
        <ImpactStandAloneUI valence={valence} impact={impact.displayValue} impactDirection={impact.direction} />
      )}
    </MatchStatusWrapperUI>
  )
}

function MatchSummary() {
  return (
    <MatchSummaryWrapperUI>
      <WinLossBadge />
      <RatingChangeBadge />
    </MatchSummaryWrapperUI>
  )
}

function EventDetailsContents() {
  const { eventName: name, eventDate: date, eventLocation: location } = useMatch()

  return <MatchItemEventDetailsUI name={name} date={date} location={location} />
}

function EventDetails() {
  return (
    <MatchItemEventDetailsWrapperUI>
      <EventDetailsContents />
    </MatchItemEventDetailsWrapperUI>
  )
}

function MatchItemHeader() {
  return (
    <>
      <MatchSummary />
      <EventDetails />
    </>
  )
}
function MatchItemBody() {
  const { isExpanded } = useMatch()
  return <>{isExpanded ? <MatchBodyExpanded /> : <MatchBodyCollapsed />}</>
}

function TeamExpandedPlayerRatingChange() {
  const { rawMatchData } = useMatch()
  const { player, postMatchRating, valence, preMatchRating, impact } = useMatchPlayer()
  const isRatingLocked = useMatchRatingLock(rawMatchData)

  if (isRatingLocked(player?.id)) {
    return (
      <TeamExpandedPlayerRatingChangeWrapperUI>
        <LockedRatingUI size="large" />
      </TeamExpandedPlayerRatingChangeWrapperUI>
    )
  }

  return (
    <TeamExpandedPlayerRatingChangeWrapperUI>
      <RatingBlockUI valence={'neutral'} rating={preMatchRating} />
      <ImpactInterimUI valence={valence} impact={impact.displayValue} />
      <RatingBlockUI valence={valence} rating={postMatchRating} />
    </TeamExpandedPlayerRatingChangeWrapperUI>
  )
}

function TeamExpandedPlayer({ playerSlot }: { playerSlot: number }) {
  const { eventFormat } = useMatch()

  if (eventFormat === 'SINGLES' && playerSlot === 2) {
    return null
  }

  return (
    <MatchPlayerContextProvider playerSlot={playerSlot}>
      <TeamExpandedPlayerContent />
    </MatchPlayerContextProvider>
  )
}

function TeamExpandedPlayerContent() {
  const { isExpanded } = useMatch()
  const { player } = useMatchPlayer()

  const id = player.id

  const linkTo = `/dashboard/player/${id}`

  return (
    <PlayerSummaryWrapperUI isExpanded={isExpanded} linkTo={linkTo}>
      <PlayerInfoWrapperUI>
        <PlayerAvatar />
        <PlayerInfo />
      </PlayerInfoWrapperUI>
      <TeamExpandedPlayerRatingChange />
    </PlayerSummaryWrapperUI>
  )
}

function TeamExpandedPlayers() {
  return (
    <TeamExpandedPlayersWrapperUI>
      <TeamExpandedPlayer playerSlot={1} />
      <TeamExpandedPlayer playerSlot={2} />
    </TeamExpandedPlayersWrapperUI>
  )
}

function TeamExpandedSummary({ team }: { team: number }) {
  const { team1, team2 } = useMatch()

  return (
    <MatchTeamContextProvider
      teamSlot={team}
      team={team === 1 ? team1 : team2}
      oppositeTeam={team === 1 ? team2 : team1}
    >
      <TeamExpandedSummaryWrapperUI team={team}>
        <TeamExpandedPlayers />
        <TeamScore />
      </TeamExpandedSummaryWrapperUI>
    </MatchTeamContextProvider>
  )
}

function MatchBodyExpanded() {
  return (
    <TeamsExpandedSummaryWrapperUI>
      <TeamExpandedSummary team={1} />
      <TeamExpandedSummary team={2} />
    </TeamsExpandedSummaryWrapperUI>
  )
}

function PlayerAvatar() {
  const { player } = useMatchPlayer()
  const { isExpanded } = useMatch()
  const avatarUrl = player.imageUrl

  return <PlayerAvatarUI avatarUrl={avatarUrl} isExpanded={isExpanded} />
}

function PlayerInfo() {
  const { player, valence, playerProfile } = useMatchPlayer()
  const { matchOwner, subjectId, isExpanded } = useMatch()

  const id = player.id
  const name = player.fullName
  const isCurrentUser = id === subjectId
  return (
    <PlayerInfoIntetrnalWrapperUI>
      <PlayerInfoUI
        name={name}
        valence={valence}
        id={id}
        matchOwnerId={matchOwner}
        isCurrentUser={isCurrentUser}
        isExpanded={isExpanded}
      />
      {isExpanded && (
        <PlayerProfileDataUI
          age={playerProfile.data?.age}
          shortAddress={playerProfile.data?.shortAddress}
          gender={playerProfile.data?.gender}
        />
      )}
    </PlayerInfoIntetrnalWrapperUI>
  )
}

function TeamCollapsedPlayerRatingAndImpact() {
  const { eloCalculated, rawMatchData } = useMatch()
  const { player, postMatchRating, valence } = useMatchPlayer()
  const isRatingLocked = useMatchRatingLock(rawMatchData)

  if (eloCalculated && isRatingLocked(player?.id)) {
    return <LockedRatingUI />
  }

  return (
    <TeamCollapsedPlayerRatingAndImpactUI
      calculated={eloCalculated}
      valence={valence}
      postMatchRating={postMatchRating}
    />
  )
}

function PlayerSummaryContent() {
  const { isExpanded } = useMatch()

  const { player } = useMatchPlayer()

  const id = player.id

  const linkTo = `/dashboard/player/${id}`

  return (
    <>
      <PlayerSummaryWrapperUI isExpanded={isExpanded} linkTo={linkTo}>
        <PlayerInfoWrapperUI>
          <PlayerAvatar />
          <PlayerInfo />
        </PlayerInfoWrapperUI>
        <TeamCollapsedPlayerRatingAndImpact />
      </PlayerSummaryWrapperUI>
    </>
  )
}

function PlayerCollapsedSummary({ playerSlot }: { playerSlot: number }) {
  const { eventFormat } = useMatch()

  if (eventFormat === 'SINGLES' && playerSlot === 2) {
    return null
  }

  return (
    <MatchPlayerContextProvider playerSlot={playerSlot}>
      <PlayerSummaryContent />
    </MatchPlayerContextProvider>
  )
}

function TeamScore() {
  const { team, oppositeTeam, teamSlot } = useMatchTeam()
  const { isExpanded } = useMatch()

  const size = isExpanded ? 'large' : 'small'

  const win = team.winner

  return (
    <TeamScoreWrapperUI isExpanded={isExpanded} teamSlot={teamSlot}>
      <TeamGameScoreUI score={team.game1} won={oppositeTeam.game1 <= team.game1} size={size} />
      <TeamGameScoreUI score={team.game2} won={oppositeTeam.game2 <= team.game2} size={size} />
      <TeamGameScoreUI score={team.game3} won={oppositeTeam.game3 <= team.game3} size={size} />
      <TeamGameScoreUI score={team.game4} won={oppositeTeam.game4 <= team.game4} size={size} />
      <TeamGameScoreUI score={team.game5} won={oppositeTeam.game5 <= team.game5} size={size} />
      <TeamCollapsedSummaryScoreWinnerUI win={win} />
    </TeamScoreWrapperUI>
  )
}

function TeamCollapsedSummaryContent() {
  return (
    <TeamCollapsedSummaryWrapperUI>
      <TeamCollapsedSummaryPlayersWrapperUI>
        <PlayerCollapsedSummary playerSlot={1} />
        <PlayerCollapsedSummary playerSlot={2} />
      </TeamCollapsedSummaryPlayersWrapperUI>
      <TeamCollapsedSummaryScoreWrapperUI>
        <TeamScore />
      </TeamCollapsedSummaryScoreWrapperUI>
    </TeamCollapsedSummaryWrapperUI>
  )
}

function TeamCollapsedSummary({ team }: { team: number }) {
  const { team1, team2 } = useMatch()

  return (
    <MatchTeamContextProvider
      teamSlot={team}
      team={team === 1 ? team1 : team2}
      oppositeTeam={team === 1 ? team2 : team1}
    >
      <TeamCollapsedSummaryContent />
    </MatchTeamContextProvider>
  )
}

function MatchBodyCollapsed() {
  return (
    <TeamsCollapsedSummaryWrapperUI>
      <TeamCollapsedSummary team={1} />
      <TeamCollapsedSummary team={2} />
    </TeamsCollapsedSummaryWrapperUI>
  )
}

function MatchItemFooter() {
  return (
    <FooterContentWrapperUI>
      <MatchId />
      <MatchDetailsToggler />
    </FooterContentWrapperUI>
  )
}

function MatchDetailsToggler() {
  const { isExpanded, toggleExpand } = useMatch()

  return <MatchDetailsTogglerUI isExpanded={isExpanded} toggleExpand={toggleExpand} />
}

function MatchId() {
  const { displayIdentity } = useMatch()

  return <MatchIdUI id={displayIdentity} />
}
